import { useState, useEffect, useRef } from 'react';
import styles from './styles.module.scss';

const VideoModal = ({ isOpen, onClose, videoSrc }: any) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.play();
      }
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const toggleMute = () => {
    setIsMuted((prevState) => !prevState);
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
    }
  };

  const handlePlayPause = () => {
    const videoElement = videoRef.current;
    if (videoElement) {
      if (isPaused) {
        videoElement.play();
      } else {
        videoElement.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  const VideoPlayButtonIcon = (
    <svg viewBox="0 0 200 148" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask
        id="mask0"
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="200"
        height="148"
      >
        <path
          fill="#fff"
          fillRule="evenodd"
          d="M200 0H0v148h200V0zM56 37a6 6 0 0 0-6 6v62a6 6 0 0 0 6 6h88a6 6 0 0 0 6-6V43a6 6 0 0 0-6-6H56z"
        />
      </mask>
      <g mask="url(#mask0)" fillOpacity="1">
        <rect
          fill="#006633"
          x="50"
          y="37"
          width="100"
          height="74"
          rx="6"
          className="pulse"
        />
        <rect
          stroke="#006633"
          strokeWidth="5"
          x="52.5"
          y="39.5"
          width="95"
          height="69"
          rx="3.5"
          className="shine"
        />
      </g>
      <path
        fill="#006633"
        fillOpacity="1"
        fillRule="evenodd"
        d="M56 37a6 6 0 0 0-6 6v62a6 6 0 0 0 6 6h88a6 6 0 0 0 6-6V43a6 6 0 0 0-6-6H56zm66 37L89 55v38l33-19z"
      />
      <path fill="#FFFFFF" d="M89 93V55l33 19-33 19z" />
    </svg>
  );

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={() => handlePlayPause()}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>

        {/* Mute Button */}
        <button className={styles.muteButton} onClick={toggleMute}>
          {isMuted ? (
            <svg
              fill="#006633"
              width="30px"
              height="30px"
              viewBox="0 0 36 36"
              version="1.1"
              preserveAspectRatio="xMidYMid meet"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title>volume-mute-solid</title>
              <path
                className="clr-i-solid clr-i-solid-path-1"
                d="M24.87,6.69A12.42,12.42,0,0,1,28.75,26.3l1.42,1.42A14.43,14.43,0,0,0,25.74,4.88a1,1,0,0,0-.87,1.8Z"
              ></path>
              <path
                className="clr-i-solid clr-i-solid-path-2"
                d="M27.3,27.67h0l-3.84-3.84-.57-.57h0L4.63,5,3.21,6.41,8.8,12H3a1,1,0,0,0-1,1V23a1,1,0,0,0,1,1H8.83l9.51,8.3A1,1,0,0,0,20,31.55V23.2l5.59,5.59c-.17.1-.34.2-.51.29a1,1,0,0,0,.9,1.79c.37-.19.72-.4,1.08-.62l2.14,2.14L30.61,31l-3.25-3.25Z"
              ></path>
              <path
                className="clr-i-solid clr-i-solid-path-3"
                d="M22.69,12.62A6.27,6.27,0,0,1,25.8,18a6.17,6.17,0,0,1-1.42,3.92l1.42,1.42a8.16,8.16,0,0,0,2-5.34,8.28,8.28,0,0,0-4.1-7.11,1,1,0,1,0-1,1.73Z"
              ></path>
              <path
                className="clr-i-solid clr-i-solid-path-4"
                d="M20,4.62a1,1,0,0,0-1.66-.75l-6.42,5.6L20,17.54Z"
              ></path>
              <rect x="0" y="0" width="36" height="36" fillOpacity="0" />
            </svg>
          ) : (
            <svg
              width="30px"
              height="30px"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
              fill="#006633"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.5 4.83h2.79L8.15 1l.85.35v13l-.85.33-3.86-3.85H1.5l-.5-.5v-5l.5-.5zM4.85 10L8 13.14V2.56L4.85 5.68l-.35.15H2v4h2.5l.35.17zM15 7.83a6.97 6.97 0 0 1-1.578 4.428l-.712-.71A5.975 5.975 0 0 0 14 7.83c0-1.4-.48-2.689-1.284-3.71l.712-.71A6.971 6.971 0 0 1 15 7.83zm-2 0a4.978 4.978 0 0 1-1.002 3.004l-.716-.716A3.982 3.982 0 0 0 12 7.83a3.98 3.98 0 0 0-.713-2.28l.716-.716c.626.835.997 1.872.997 2.996zm-2 0c0 .574-.16 1.11-.44 1.566l-.739-.738a1.993 1.993 0 0 0 .005-1.647l.739-.739c.276.454.435.988.435 1.558z"
              />
            </svg>
          )}
        </button>

        <div className={styles.videoWrapper} onClick={() => handlePlayPause()}>
          <video
            ref={videoRef}
            src={videoSrc}
            className={styles.modalVideo}
            muted={isMuted}
            controls={false}
            autoPlay
          />
          {isPaused && (
            <div className={styles.playButton} onClick={handlePlayPause}>
              <div className={styles.playBtnWrapper}>{VideoPlayButtonIcon}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
